import { ReactNode, createContext, useContext, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import "./Modal.css";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Opt-in only (Daily Entry Audit History UI Polish) — every existing Modal
   * consumer keeps today's exact 480px width and single-block layout unless
   * it explicitly passes `size="lg"`. "lg" additionally caps the dialog to a
   * sensible viewport height with an internally scrolling body (header/
   * footer stay pinned), for content that can be long — e.g. audit history.
   */
  size?: "default" | "lg";
  /**
   * Opt-in only — no existing Modal consumer renders a close button unless
   * it explicitly passes this, so nothing about other modals changes here.
   */
  showCloseButton?: boolean;
  /** Required when `showCloseButton` is true — kept out of the shared primitive so the label stays translated at the call site (this codebase's established i18n architecture never embeds translated text in a shared primitive). */
  closeButtonLabel?: string;
}

/**
 * React Aria overlays (SearchCombobox's ComboBox popover, DateField's
 * calendar popover) default to portalling into `document.body`. Inside a
 * Radix Dialog that makes their DOM a *sibling* of Dialog.Content, not a
 * descendant — Radix's DismissableLayer then treats pointer interaction
 * inside them as "outside the dialog," racing the click against dismiss/
 * focus-trap behavior and making option selection unreliable (the
 * production combobox bug — NORMAX_PERFORMANCE_NORM_FORM_UX_FIX_REPORT.md).
 * Modal exposes Dialog.Content's own DOM node so nested overlays can portal
 * INTO it instead, becoming real descendants — see SearchCombobox.tsx /
 * DateField.tsx's `UNSTABLE_portalContainer` use.
 */
const ModalPortalContext = createContext<HTMLElement | undefined>(undefined);

export function useModalPortalContainer(): HTMLElement | undefined {
  return useContext(ModalPortalContext);
}

export function Modal({
  open,
  onOpenChange,
  title,
  children,
  footer,
  size = "default",
  showCloseButton = false,
  closeButtonLabel,
}: ModalProps) {
  const [portalNode, setPortalNode] = useState<HTMLDivElement | null>(null);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="nx-modal__overlay" />
        <Dialog.Content
          className={["nx-modal__content", size === "lg" ? "nx-modal__content--lg" : null]
            .filter(Boolean)
            .join(" ")}
          ref={setPortalNode}
        >
          <div className="nx-modal__header">
            <Dialog.Title className="nx-modal__title">{title}</Dialog.Title>
            {showCloseButton ? (
              // Escape-to-close and outside-click-to-close are Radix Dialog
              // defaults, untouched here — this is an ADDITIONAL, explicit
              // close affordance, not a replacement for them.
              <Dialog.Close asChild>
                <button type="button" className="nx-modal__close" aria-label={closeButtonLabel}>
                  <span aria-hidden="true">×</span>
                </button>
              </Dialog.Close>
            ) : null}
          </div>
          <ModalPortalContext.Provider value={portalNode ?? undefined}>
            <div className="nx-modal__body">{children}</div>
            {footer ? <div className="nx-modal__footer">{footer}</div> : null}
          </ModalPortalContext.Provider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
